import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import publicApiService from '../services/api/publicApi';
import tokenValidator from '../utils/tokenValidator';
import { roleConfig } from '../config/roleConfig';
import './Login.css';

/**
 * The route to return to after logging in, or null to fall back to the role's default.
 *
 * WHY THIS IS GUARDED. `from` is a react-router location object saved by `AuthGuard`, so today it is
 * ours and safe. A post-login redirect target read out of navigation state is nevertheless the exact
 * shape that becomes an open redirect the moment anything else learns to write it — a "return to"
 * query param, an email link, a deep link from a notification. One check now costs nothing.
 *
 * Accepts only a same-origin ABSOLUTE PATH. `//evil.com` is rejected explicitly: it is protocol-
 * relative, so a browser reads it as another host even though it starts with a slash. `/\evil.com`
 * is rejected for the same reason — several browsers normalise the backslash to a forward slash
 * before parsing, so it is the same attack wearing a different character. `/login` is rejected too:
 * returning someone to the login page after logging in is a loop, not a destination.
 */
const safeReturnPath = (from) => {
  const pathname = from?.pathname;
  if (typeof pathname !== 'string') return null;
  if (!pathname.startsWith('/') || pathname.startsWith('//') || pathname.startsWith('/\\')) return null;
  if (pathname === '/login') return null;
  // Query and hash are preserved because they carry the payload: `/inbox?c=<id>&n=1` without its
  // query is just the inbox, which is precisely the navigation step push exists to remove.
  return `${pathname}${from.search || ''}${from.hash || ''}`;
};

const Login = ({ setUser }) => {
  const authContext = useContext(AuthContext);
  const { login } = authContext || {};
  const navigate = useNavigate();
  const location = useLocation();
  // A remembered email is the tick state — one key, so the two can never disagree.
  // Never the password.
  const [email, setEmail] = useState(() => localStorage.getItem('rememberedEmail') || "");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('rememberedEmail'));
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(location.state?.message || "");

  useEffect(() => {
    // Tell the user their session ran out, rather than bouncing them here silently.
    // One-shot: a deliberate logout never sets this.
    if (tokenValidator.consumeSessionExpired()) {
      setError("انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى");
    }
  }, []);

  useEffect(() => {
    // Auto-dismiss success message after 5 seconds
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

const handleLogin = async (e) => {
  e.preventDefault();

  if (!email || !password) {
    setError("يرجى تعبئة جميع الحقول بشكل صحيح.");
    return;
  }

  try {
    // Use the publicApiService for login (no auth headers needed)
    // Industry standard: Use dedicated API instance for auth endpoints
    const data = await publicApiService.login(email, password, rememberMe);

    // v3.0: Backend returns accessToken + refreshToken
    const token = data.accessToken || data.token;
    const refreshToken = data.refreshToken;

    // Pre-fill this email next visit, or forget it if the box was unticked
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    // Whether reopening the app later may renew silently (see tokenValidator.rememberSession)
    tokenValidator.rememberSession(rememberMe);

    // Use the AuthContext login function to properly store all user data
    if (login) {
      login(token, {
        userId: data.user?.userId || data.userId,
        email: data.user?.email || data.email,
        role: data.user?.role || data.role,
        name: data.user?.name || data.name,
        locale: data.user?.locale || data.locale
      }, refreshToken);
    } else {
      // Fallback when AuthContext is unavailable — same single writer, no local copy
      tokenValidator.beginSession();
      tokenValidator.storeTokenPair({ accessToken: token, refreshToken });

      localStorage.setItem('userId', data.user?.userId || data.userId);
      localStorage.setItem('userRole', data.user?.role || data.role);
      localStorage.setItem('userName', data.user?.name || data.name);
    }

    const role = data.user?.role || data.role;
    const destination = roleConfig[role]?.defaultRoute || '/dashboard';
    // Go back to what they were actually trying to reach, not to the role's front page.
    //
    // `AuthGuard` has always saved it (`state:{from: location}`) and this page has always ignored
    // it. That was invisible while every arrival here was someone typing a password at the start of
    // a shift — but a push notification tapped on a phone left overnight lands on an EXPIRED
    // session, so the saved destination is the common path, not the edge case (D-a).
    navigate(safeReturnPath(location.state?.from) || destination);
  } catch (err) {
    // Handle different error scenarios based on status code
    if (err.response) {
      // Server responded with error status
      const errorMessage = err.response.data?.error || err.response.data?.message || 'فشل تسجيل الدخول';
      setError(errorMessage);
    } else if (err.request) {
      // No response received
      setError("لا يمكن الاتصال بالخادم. تحقق من اتصال الإنترنت");
    } else {
      // Other errors
      setError("حدث خطأ: " + (err.message || 'خطأ غير معروف'));
    }
  }
};

  // CSS-in-JS styles object for better organization
  const styles = {
    pageContainer: {
      fontFamily: "'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: 'radial-gradient(circle at 15% 20%, rgba(255, 204, 0, 0.08), transparent 30%), radial-gradient(circle at 85% 80%, rgba(107, 70, 193, 0.16), transparent 35%), linear-gradient(135deg, #0B0B1A 0%, #0F0F23 55%, #15152A 100%)',
      color: '#1F2937',
      minHeight: '100vh',
      padding: '32px 18px',
      direction: 'rtl',
    },
    shell: {
      maxWidth: '1180px',
      margin: '0 auto',
      background: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.04)',
      borderRadius: '18px',
      overflow: 'hidden',
      boxShadow: '0 20px 45px rgba(0, 0, 0, 0.18)',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      minHeight: 'calc(100vh - 64px)',
    },
    brand: {
      position: 'relative',
      background: 'linear-gradient(145deg, rgba(15, 15, 35, 0.9), rgba(21, 21, 42, 0.85))',
      color: '#FFFFFF',
      padding: 'clamp(28px, 5vw, 54px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '28px',
      isolation: 'isolate',
    },
    brandOverlayBefore: {
      content: '""',
      position: 'absolute',
      borderRadius: '50%',
      opacity: 0.6,
      zIndex: 0,
      width: '240px',
      height: '240px',
      background: 'rgba(107, 70, 193, 0.25)',
      top: '-60px',
      right: '-40px',
      pointerEvents: 'none',
    },
    brandOverlayAfter: {
      content: '""',
      position: 'absolute',
      borderRadius: '50%',
      opacity: 0.6,
      zIndex: 0,
      width: '280px',
      height: '280px',
      background: 'rgba(255, 204, 0, 0.2)',
      bottom: '-80px',
      left: '-60px',
      pointerEvents: 'none',
    },
    brandContent: {
      position: 'relative',
      zIndex: 1,
      maxWidth: '520px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      textAlign: 'right',
    },
    brandLogo: {
      width: '220px',
      height: 'auto',
      marginBottom: '24px',
    },
    pill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 14px',
      background: 'rgba(255, 255, 255, 0.06)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '999px',
      fontSize: '14px',
      marginBottom: '12px',
    },
    pillIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '26px',
      height: '26px',
      borderRadius: '50%',
      background: 'rgba(255, 204, 0, 0.16)',
      color: '#FFCC00',
    },
    brandTitle: {
      fontSize: 'clamp(28px, 4vw, 38px)',
      lineHeight: 1.3,
      marginBottom: '18px',
      fontWeight: 700,
    },
    featureList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '14px',
      marginTop: '18px',
    },
    featureCard: {
      background: 'rgba(255, 255, 255, 0.06)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '14px',
      padding: '14px 16px',
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start',
      transition: 'transform 0.22s ease, border-color 0.22s ease',
    },
    featureIcon: {
      width: '34px',
      height: '34px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, rgba(255, 204, 0, 0.2), rgba(107, 70, 193, 0.28))',
      color: '#fff',
      flexShrink: 0,
      fontSize: '18px',
    },
    featureText: {
      fontSize: '15px',
      color: 'rgba(255, 255, 255, 0.92)',
      margin: 0,
    },
    formSide: {
      background: 'linear-gradient(180deg, #FFFFFF 0%, #F6F7FB 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 'clamp(24px, 5vw, 56px)',
      position: 'relative',
    },
    formWrapper: {
      background: '#FFFFFF',
      borderRadius: '18px',
      padding: 'clamp(22px, 4vw, 32px)',
      width: 'min(440px, 100%)',
      boxShadow: '0 10px 30px rgba(107, 70, 193, 0.08)',
      border: '1px solid #E5E7EB',
    },
    formTitle: {
      fontSize: '24px',
      color: '#1F2937',
      marginBottom: '6px',
      fontWeight: 700,
    },
    formSubtitle: {
      color: '#6B7280',
      fontSize: '15px',
      marginBottom: '18px',
    },
    alert: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 14px',
      borderRadius: '12px',
      fontSize: '14px',
      marginBottom: '14px',
    },
    alertError: {
      color: '#B91C1C',
      background: 'rgba(185, 28, 28, 0.08)',
      border: '1px solid rgba(185, 28, 28, 0.2)',
    },
    alertSuccess: {
      color: '#0F9D58',
      background: 'rgba(15, 157, 88, 0.08)',
      border: '1px solid rgba(15, 157, 88, 0.2)',
    },
    field: {
      marginBottom: '16px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: 600,
      color: '#1F2937',
      marginBottom: '8px',
    },
    inputShell: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      width: '100%',
      padding: '14px 44px 14px 14px',
      borderRadius: '14px',
      border: '1.5px solid #E5E7EB',
      background: '#FAFBFF',
      fontSize: '15px',
      fontFamily: 'inherit',
      transition: 'border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease',
      outline: 'none',
      direction: 'rtl',
      textAlign: 'right',
    },
    inputFocus: {
      borderColor: '#6B46C1',
      background: '#FFFFFF',
      boxShadow: '0 0 0 4px rgba(107, 70, 193, 0.12)',
    },
    rowBetween: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      margin: '8px 0 16px',
    },
    remember: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: '#6B7280',
    },
    link: {
      color: '#6B46C1',
      fontWeight: 600,
      textDecoration: 'none',
      transition: 'color 0.22s ease',
      fontSize: '14px',
    },
    button: {
      width: '100%',
      padding: '14px',
      border: 'none',
      borderRadius: '14px',
      fontSize: '16px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'transform 0.22s ease, box-shadow 0.22s ease',
      background: 'linear-gradient(135deg, #6B46C1 0%, #55309E 100%)',
      color: '#FFFFFF',
      boxShadow: '0 12px 30px rgba(107, 70, 193, 0.25)',
    },
    footnote: {
      textAlign: 'center',
      fontSize: '14px',
      color: '#6B7280',
      marginTop: '18px',
    },
  };

  return (
    <div style={styles.pageContainer}>
      <main style={styles.shell}>
        <div style={styles.grid} className="login-grid">
          {/* Brand Side */}
          <section style={styles.brand}>
            <div style={styles.brandOverlayBefore}></div>
            <div style={styles.brandOverlayAfter}></div>
            <div style={styles.brandContent}>
              <img src="/images/Host Me logo 2.png" alt="Host Me" width={220} height={175} style={styles.brandLogo} />
              <div style={styles.pill}>
                <span style={styles.pillIcon}>AI</span>
                منصة متكاملة مدعومة بالذكاء الاصطناعي
              </div>
              <h1 style={styles.brandTitle}>إدارة وتأجير وحداتك بسهولة وكفاءة</h1>
              <div style={styles.featureList}>
                <article style={styles.featureCard}>
                  <div style={styles.featureIcon}>✓</div>
                  <p style={styles.featureText}>مزامنة تلقائية مع المنصات لتجنب تعارض الحجوزات</p>
                </article>
                <article style={styles.featureCard}>
                  <div style={styles.featureIcon}>✓</div>
                  <p style={styles.featureText}>تقارير شاملة عن الإيرادات والإشغال والأرباح</p>
                </article>
                <article style={styles.featureCard}>
                  <div style={styles.featureIcon}>✓</div>
                  <p style={styles.featureText}>توصيات تسعير ذكية مبنية على الإشغال، المواسم، والطلب المتوقع</p>
                </article>
              </div>
            </div>
          </section>

          {/* Form Side */}
          <section style={styles.formSide}>
            <div style={styles.formWrapper}>
              <div>
                <h2 style={styles.formTitle}>مرحباً بك مجدداً</h2>
                <p style={styles.formSubtitle}>سجل الدخول للوصول إلى لوحة التحكم</p>
              </div>

              {/* Success Alert */}
              {successMessage && (
                <div style={{...styles.alert, ...styles.alertSuccess}}>
                  <span aria-hidden="true">✓</span>
                  <span>{successMessage}</span>
                </div>
              )}

              {/* Error Alert */}
              {error && (
                <div style={{...styles.alert, ...styles.alertError}}>
                  <span aria-hidden="true">!</span>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleLogin} noValidate>
                {/* Email Field */}
                <div style={styles.field}>
                  <label htmlFor="email" style={styles.label}>البريد الإلكتروني</label>
                  <div style={styles.inputShell}>
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{
                      position: 'absolute',
                      left: '14px',
                      width: '20px',
                      height: '20px',
                      color: '#6B7280',
                      pointerEvents: 'none',
                    }}>
                      <path fill="currentColor" d="M20 4H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V6c0-1.1-.9-2-2-2Zm0 2v.01L12 12 4 6.01V6h16ZM4 18V8l8 6 8-6v10H4Z"/>
                    </svg>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="أدخل بريدك الإلكتروني"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={styles.input}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#6B46C1';
                        e.target.style.background = '#FFFFFF';
                        e.target.style.boxShadow = '0 0 0 4px rgba(107, 70, 193, 0.12)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                        e.target.style.background = '#FAFBFF';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div style={styles.field}>
                  <label htmlFor="password" style={styles.label}>كلمة المرور</label>
                  <div style={styles.inputShell}>
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{
                      position: 'absolute',
                      left: '14px',
                      width: '20px',
                      height: '20px',
                      color: '#6B7280',
                      pointerEvents: 'none',
                    }}>
                      <path fill="currentColor" d="M17 8V7a5 5 0 1 0-10 0v1H5v13h14V8h-2Zm-8-1a3 3 0 0 1 6 0v1H9V7Zm8 12H7V10h10v9Z"/>
                    </svg>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      placeholder="أدخل كلمة المرور"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={styles.input}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#6B46C1';
                        e.target.style.background = '#FFFFFF';
                        e.target.style.boxShadow = '0 0 0 4px rgba(107, 70, 193, 0.12)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                        e.target.style.background = '#FAFBFF';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div style={styles.rowBetween}>
                  <label style={styles.remember}>
                    <input
                      type="checkbox"
                      id="remember"
                      name="remember"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                      }}
                    />
                    <span>تذكرني</span>
                  </label>
                  <a
                    href="#/reset-password-request"
                    style={styles.link}
                    onMouseOver={(e) => e.target.style.color = '#55309E'}
                    onMouseOut={(e) => e.target.style.color = '#6B46C1'}
                  >
                    نسيت كلمة المرور؟
                  </a>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  style={styles.button}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 14px 36px rgba(107, 70, 193, 0.32)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 12px 30px rgba(107, 70, 193, 0.25)';
                  }}
                  onMouseDown={(e) => {
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  تسجيل الدخول
                </button>

                {/* Sign Up Link */}
                <p style={styles.footnote}>
                  ليس لديك حساب؟{' '}
                  <a
                    href="https://hostme.sa/contact-us/"
                    target="_blank"
                    rel="noreferrer"
                    style={styles.link}
                    onMouseOver={(e) => e.target.style.color = '#55309E'}
                    onMouseOut={(e) => e.target.style.color = '#6B46C1'}
                  >
                    تواصل معنا
                  </a>
                </p>
              </form>
            </div>
          </section>
        </div>
      </main>

    </div>
  );
};

export default Login;
